// EmailPreviewModal -- shows the actual sent email (subject, from, to,
// cc, HTML body) for a given documents.id. Fetches from
// GET /api/emails/:id/preview which in turn hits Resend's API to get
// the real body.
//
// Dave 2026-07-09: 'Any email updating a customer from command I need
// to be able to review it.' The View button anywhere in the app opens
// this modal and shows what actually went out.

import { useEffect, useState } from 'react';
import axios from 'axios';

function fmt(v) {
  if (v == null || v === '') return '';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}
function fmtIso(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function EmailPreviewModal({ documentId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [data, setData]       = useState(null);
  const [fetchWarning, setFetchWarning] = useState(null);

  useEffect(() => {
    if (!documentId) return;
    setLoading(true);
    setError(null);
    setFetchWarning(null);
    axios.get(`/api/emails/${documentId}/preview`)
      .then((r) => {
        setData(r.data);
        if (r.data?.fetch_error) setFetchWarning(r.data.fetch_error);
      })
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [documentId]);

  if (!documentId) return null;

  const doc = data?.document || {};
  const email = data?.email || null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1200, padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 8, maxWidth: 900, width: '100%',
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: 'var(--space-4)', borderBottom: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h3 style={{ margin: 0 }}>Sent Email</h3>
            {doc.company && (
              <div style={{ color: 'var(--color-text-dim)', fontSize: 12, marginTop: 4 }}>
                To customer: <strong>{doc.company}</strong>
                {doc.sender_name && <> · Sent by <strong>{doc.sender_name}</strong></>}
                {doc.sent_at && <> · <span className="cell-mono">{fmtIso(doc.sent_at)}</span></>}
              </div>
            )}
          </div>
          <button type="button" className="btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>

        {/* Body */}
        <div style={{ padding: 'var(--space-4)', overflow: 'auto', flex: 1 }}>
          {loading && <div>Loading email from Resend...</div>}
          {error   && <div style={{ color: 'var(--color-bad)' }}>Error: {error}</div>}

          {!loading && !error && email && (
            <>
              {/* Envelope */}
              <div style={{
                background: 'var(--color-surface-sunken, #f8fafc)',
                border: '1px solid var(--color-border)', borderRadius: 6,
                padding: 12, fontSize: 12, marginBottom: 16,
              }}>
                <HeaderRow label="From"    value={fmt(email.from)} />
                <HeaderRow label="To"      value={fmt(email.to)} />
                {email.cc?.length  > 0 && <HeaderRow label="Cc"  value={fmt(email.cc)} />}
                {email.bcc?.length > 0 && <HeaderRow label="Bcc" value={fmt(email.bcc)} />}
                <HeaderRow label="Subject" value={email.subject || '(no subject)'} bold />
                {email.last_event && <HeaderRow label="Status" value={email.last_event} muted />}
              </div>

              {/* Body */}
              {email.html ? (
                <div style={{
                  border: '1px solid var(--color-border)', borderRadius: 6,
                  padding: 16, background: '#fff',
                }}>
                  {/* Sandbox the HTML so any inline styles / scripts in the
                      email can't affect the app. Height auto-grows via iframe
                      resize on load. */}
                  <iframe
                    title="Sent email body"
                    srcDoc={email.html}
                    style={{ width: '100%', minHeight: 420, border: 'none' }}
                    sandbox=""
                  />
                </div>
              ) : email.text ? (
                <pre style={{
                  whiteSpace: 'pre-wrap', wordWrap: 'break-word',
                  padding: 16, background: '#f9fafb',
                  border: '1px solid var(--color-border)', borderRadius: 6,
                  fontFamily: 'var(--font-mono)', fontSize: 13,
                }}>{email.text}</pre>
              ) : (
                <div style={{ color: 'var(--color-text-dim)' }}>No body content returned.</div>
              )}
            </>
          )}

          {!loading && !error && !email && (
            <div>
              <div style={{
                background: 'var(--color-warn-bg, #fef3c7)', color: 'var(--color-warn, #b45309)',
                padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 13,
              }}>
                Couldn't fetch the email body from Resend
                {fetchWarning && <>: {fetchWarning}</>}.
                This usually means the email was sent before your Resend retention window,
                or the message id got dropped. What we do have on file for this send:
              </div>
              <dl style={{ fontSize: 13, display: 'grid', gridTemplateColumns: '160px 1fr', gap: 4 }}>
                <dt><strong>Customer</strong></dt><dd>{doc.company || '-'}</dd>
                <dt><strong>Recipient</strong></dt><dd>{doc.recipient_email || '-'}</dd>
                <dt><strong>Sender</strong></dt><dd>{doc.sender_name || '-'}</dd>
                <dt><strong>Type</strong></dt><dd>{doc.type || '-'}</dd>
                <dt><strong>Local label</strong></dt><dd className="cell-mono">{doc.file_path || '-'}</dd>
                <dt><strong>Sent at</strong></dt><dd>{fmtIso(doc.sent_at)}</dd>
                <dt><strong>Opened at</strong></dt><dd>{doc.opened_at ? fmtIso(doc.opened_at) : '(never)'}</dd>
                <dt><strong>Resend message id</strong></dt><dd className="cell-mono" style={{ fontSize: 11 }}>{doc.resend_message_id || '-'}</dd>
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function HeaderRow({ label, value, bold, muted }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: 8, padding: '3px 0' }}>
      <div style={{
        color: 'var(--color-text-dim)', textTransform: 'uppercase',
        fontSize: 10, fontFamily: 'var(--font-mono)', letterSpacing: '0.04em',
      }}>{label}</div>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 12, wordBreak: 'break-word',
        fontWeight: bold ? 700 : 400, color: muted ? 'var(--color-text-dim)' : 'var(--color-text)',
      }}>{value}</div>
    </div>
  );
}
